import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Package, Loader2, MailCheck, Lock } from "lucide-react";

import { _t } from "@/lib/i18n";
export default function InviteAccept() {
  const { invitation_id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${invitation_id}/preview`, { params: { token } });
        setPreview(data);
      } catch (err) {
        setError(err?.response?.status === 410 ? "expired" : "invalid");
      }
    })();
  }, [invitation_id, token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invitations/${invitation_id}/accept`, { token });
      toast.success(data.already_accepted ? _t("Invito già associato al tuo account.") : _t("Capsula collegata al tuo account!"));
      nav(`/capsules/${data.capsule_id}`);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 403) toast.error(_t("Questo invito è per un altro indirizzo email. Esci e accedi con l'account corretto."));
      else if (st === 410) toast.error(_t("Invito scaduto o revocato."));
      else toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0b0d] px-4">
      <div className="tc-card p-8 max-w-md text-center">
        <h1 className="text-white text-xl mb-3">{_t("Invito {0}", error === "expired" ? "scaduto" : _t("non valido"))}</h1>
        <p className="text-white/60 text-sm">{_t("Contatta chi ti ha invitato per ricevere un nuovo link.")}</p>
      </div>
    </div>
  );
  if (!preview) return <div className="min-h-screen flex items-center justify-center bg-[#0b0b0d] text-white/60">{_t("Verifico invito…")}</div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0b0d] px-4">
      <div className="tc-card p-8 md:p-10 w-full max-w-lg" data-testid="invite-preview">
        <div className="mb-4 flex items-center gap-3">
          <Package className="w-6 h-6 text-[#FF5722]" />
          <h1 className="font-display text-2xl text-white">{_t("Sei stato invitato")}</h1>
        </div>
        <p className="text-white/80">
          <strong>{preview.sender || _t("Un mittente")}</strong>{" "}{_t("ti ha destinato una capsula del tempo:")}</p>
        <p className="text-white text-xl my-3">«{preview.capsule_title}»</p>
        <p className="text-white/60 text-sm mb-2 flex items-center gap-2">
          <Lock className="w-4 h-4" />{" "}{_t("Apertura prevista:")}{" "}<strong className="text-white">{preview.open_at?.slice(0, 10) || "—"}</strong>
        </p>
        <p className="text-white/60 text-sm mb-6 flex items-center gap-2">
          <MailCheck className="w-4 h-4" />{" "}{_t("Riservato a:")}{" "}<strong className="text-white">{preview.recipient_email}</strong>
        </p>
        {!user ? (
          <div className="space-y-3">
            <p className="text-white/70 text-sm">{_t("Accedi o registrati con l'email indicata sopra per collegare la capsula al tuo account.")}</p>
            <div className="flex gap-2">
              <Link to={`/login?redirect=/invito/${invitation_id}?token=${token}&email=${encodeURIComponent(preview.recipient_email)}`} className="btn-primary flex-1" data-testid="invite-login">{_t("Accedi")}</Link>
              <Link to={`/register?email=${encodeURIComponent(preview.recipient_email)}&redirect=/invito/${invitation_id}?token=${token}`} className="btn-ghost flex-1" data-testid="invite-register">{_t("Registrati")}</Link>
            </div>
          </div>
        ) : user.email?.toLowerCase() === preview.recipient_email ? (
          <button data-testid="invite-accept" onClick={accept} disabled={busy} className="btn-primary w-full">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" />{" "}{_t("Attendere…")}</> : _t("Accetta e vedi la capsula")}
          </button>
        ) : (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{_t("Sei autenticato come")}{" "}<strong>{user.email}</strong>{_t(", ma questo invito è per")}{" "}<strong>{preview.recipient_email}</strong>{_t(". Effettua il logout e accedi con l'account corretto.")}</div>
        )}
      </div>
    </div>
  );
}
